import React from 'react';
import i18next from "i18next";
import { connect } from 'react-redux';
import { Dropdown } from 'react-bootstrap';
import { toastr } from "react-redux-toastr";
import { FiPlus, FiMoreHorizontal, FiTrash2, FiEdit3, FiX, FiInfo } from "react-icons/fi";
import { FormGroup, FormLabel, Row, Col, FormControl, InputGroup, OverlayTrigger, Tooltip } from "react-bootstrap";
import EmployeeSeparatedRestEdit from "./EmployeeSeparatedRestEditModal";
import EmployeeRestrictedShiftEdit from "./EmployeeRestrictedShiftEditModal";
import EmployeeBackupEdit from "./EmployeeBackupEditModal";
import { showModal, hideModal } from "actions/modalActions";
import * as employeesActions from 'actions/employeesActions';
import * as shiftsActions from 'actions/shiftsActions';
import * as calendarsActions from 'actions/calendarsActions';
import { employeesService } from "services";
import { errorFormating, getContrastColor } from "utils/utils";
import { convertFromUTC, getFirstLastInterval } from "utils/datetime";
import Switch from "components/Switch";
import TimeInputWithArrows from "components/TimeInputWithArrows";
import DataTable from "components/DataTable";
import ShiftPatternShiftTag from "components/ShiftPatterns/ShiftPatternShiftTag";
import EmployeeDayTypeGrid from "./EmployeeDayTypeGrid";
import { restrictionItemHasValues, restrictionSignature } from "./restrictionGrid";

import "./EmployeeMagicPlan.scss";

const SECTION_HOURS = "hours";
const SECTION_REST = "rest";
const SECTION_SHIFTS = "shifts";

const SECTIONS = [
	{ key: SECTION_HOURS },
	{ key: SECTION_REST },
	{ key: SECTION_SHIFTS },
];


class EmployeeMagicPlan extends React.Component {
	constructor(props) {
		super(props);
		// console.log(props);
		this.closeModal = this.closeModal.bind(this);
		this.openNewEmployeeSeparatedRestModal = this.openNewEmployeeSeparatedRestModal.bind(this);
		this.openNewEmployeeBackupModal = this.openNewEmployeeBackupModal.bind(this);
		this.openDeleteEmployeeBackupModal = this.openDeleteEmployeeBackupModal.bind(this);
		this.openNewRestrictedShiftModal = this.openNewRestrictedShiftModal.bind(this);
		this.openDeleteRestrictedShiftModal = this.openDeleteRestrictedShiftModal.bind(this);
		this.openAlertModal = this.openAlertModal.bind(this);
		this.getSeparatedRestEmployees = this.getSeparatedRestEmployees.bind(this);
		this.getRestrictedShiftsEmployees = this.getRestrictedShiftsEmployees.bind(this);
		this.getSeparatedRestBackup = this.getSeparatedRestBackup.bind(this);

		const employee = { ...this.props.employee };
		// Initialize day_type_config from day_type_config_response if it doesn't exist
		if (!employee.day_type_config && employee.day_type_config_response) {
			employee.day_type_config = employee.day_type_config_response.map(item => ({
				id: item.id,
				day_type_id: item.day_type ? item.day_type.id : item.day_type_id,
				max_daily_working_hours: item.max_daily_working_hours,
				min_rest_hours_between_blocks: item.min_rest_hours_between_blocks,
				max_rest_hours_between_blocks: item.max_rest_hours_between_blocks,
				rest_window_between_blocks_start: item.rest_window_between_blocks_start,
				rest_window_between_blocks_end: item.rest_window_between_blocks_end,
				max_number_of_blocks: item.max_number_of_blocks,
			}));
		}

		if (!employee.shift_config && employee.shift_config_response) {
			employee.shift_config = employee.shift_config_response.map(item => ({
				id: item.id,
				shift_id: item.shift ? item.shift.id : item.shift_id,
				max_weekly_assignments: item.max_weekly_assignments,
				priority: item.priority,
			}));
		}

		// JSON.stringify omite undefined; el backend puede no devolver aún esta clave en el GET.
		if (employee.must_never_be_alone == null) {
			employee.must_never_be_alone = false;
		}

		if (employee.high_load == null) {
			employee.high_load = false;
		}

		this.state = {
			employee,
			prevEmployee: employee,
			errors: {},
			error_footer: "",
			hasUnsavedHoursChanges: false,
			isSavingHours: false,
			hasUnsavedShiftsChanges: false,
			isSavingShifts: false,
			new_shift_config: { shift_id: "", max_weekly_assignments: "", priority: "" },
			activeSection: SECTION_HOURS,
			maxHoursBetweenRestDraft: this.formatMaxHoursBetweenRest(
				employee.max_working_hours_between_restdays
			),
		};
	}

	setActiveSection = (key) => {
		this.setState({ activeSection: key });
	};

	async componentDidMount() {
		const loadingPromises = [
			this.getSeparatedRestEmployees(),
			this.getRestrictedShiftsEmployees(),
			this.getSeparatedRestBackup(),
			this.props.getSelectableShifts(),
		];

		// Solo cargar empleados si no están disponibles ya
		if (!this.props.select_employees || this.props.select_employees.length === 0) {
			loadingPromises.push(this.props.getSelectableEmployees());
		}

		if (!this.props.select_day_types || this.props.select_day_types.length === 0) {
			loadingPromises.push(this.props.getSelectableDayTypes());
		}

		await Promise.all(loadingPromises);
	}

	handleSwitch = (event) => {
		const { employee } = this.state;

		employee[event.target.id] = event.target.checked;

		if (!employee.enabled)
			employee.with_access = false;

		if (employee.with_access)
			employee.enabled = true;

		this.setState(
			{
				employee,
			},
			() => {
				this._saveEmployee();
			}
		);
	};

	handleBlur = async () => {
		const hasChanged = this.state.employee !== this.state.prevEmployee;

		if (hasChanged) {
			this._saveEmployee();
		}
	};

	_saveEmployee = async () => {
		const { employee } = this.state;

		const employeeToSave = { ...employee };
		delete employeeToSave.day_type_config_response;
		delete employeeToSave.shift_config_response;

		const response = await employeesService.update(employeeToSave);

		if (response.ok) {
			toastr.success(i18next.t("common.success"), i18next.t("profile.changesSaved"));
			this.publishEmployee(employee);
			this.notifyDataChanged();
			this.setState({
				prevEmployee: employee,
				errors: {},
			});
		} else {
			const formattedErrors = errorFormating(response);
			const errorMessage =
				formattedErrors?.general
				|| response?.errors?.[0]?.description
				|| i18next.t("magicPlan.saveError");
			toastr.error("Error", errorMessage);
			this.setState({
				errors: formattedErrors,
			});
		}
	};

	async getSeparatedRestEmployees() {
		const employeeId = this.props.employee.id;
		await this.props.getEmployeeSeparatedRestEmployees(employeeId);
	}

	async getRestrictedShiftsEmployees() {
		const employeeId = this.props.employee.id;
		await this.props.getEmployeeRestrictedShifts(employeeId);
	}

	async getSeparatedRestBackup() {
		const employeeId = this.props.employee.id;
		await this.props.getEmployeeSeparatedRestBackup(employeeId);
	}

	closeModal() {
		this.props.hideModal()
	}

	notifyDataChanged = () => {
		if (typeof this.props.onDataChanged === "function") {
			this.props.onDataChanged();
		}
	};

	publishEmployee = (employee) => {
		if (typeof this.props.onEmployeeUpdated === "function") {
			this.props.onEmployeeUpdated(employee);
		}
	};

	formatMaxHoursBetweenRest = (value) => {
		if (value == null || value === "") return "";
		return String(value);
	};

	handleMaxHoursBetweenRestChange = (event) => {
		const digits = event.target.value.replace(/\D/g, "").slice(0, 4);
		if (event.target.value !== digits) {
			event.target.value = digits;
		}
		this.setState({ maxHoursBetweenRestDraft: digits });
	};

	handleMaxHoursBetweenRestKeyDown = (event) => {
		if (event.key !== "Enter") return;
		event.preventDefault();
		event.currentTarget.blur();
	};

	handleMaxHoursBetweenRestBlur = () => {
		const raw = (this.state.maxHoursBetweenRestDraft || "").trim();
		const nextValue = raw === "" ? null : parseInt(raw, 10);
		const previous = this.state.prevEmployee.max_working_hours_between_restdays ?? null;
		const normalizedDraft = this.formatMaxHoursBetweenRest(nextValue);

		if (nextValue === previous) {
			if (normalizedDraft !== this.state.maxHoursBetweenRestDraft) {
				this.setState({ maxHoursBetweenRestDraft: normalizedDraft });
			}
			return;
		}

		const employee = {
			...this.state.employee,
			max_working_hours_between_restdays: nextValue,
		};

		this.setState(
			{ employee, maxHoursBetweenRestDraft: normalizedDraft },
			() => this._saveEmployee()
		);
	};

	openAlertModal(message) {
		this.props.showModal({
			open: true,
			title: i18next.t("magicPlan.errorDeleting"),
			style: { width: '400px' },
			message: message,
			closeModal: this.closeModal
		}, 'alert')
	}

	// modales descanso

	openNewEmployeeSeparatedRestModal() {
		const { employee } = this.props;

		this.props.showModal({
			open: true,
			title: i18next.t("magicPlan.newRestEmployee"),
			style: { width: '300px' },
			content: (
				<EmployeeSeparatedRestEdit
					handleClose={this.closeModal}
					getSeparatedRestEmployees={this.getSeparatedRestEmployees}
					employee={employee}
					onDataChanged={this.props.onDataChanged}
				/>
			),
			closeModal: this.closeModal
		}, 'edit')
	}

	openDeleteEmployeeSeparatedRestModal(employee_id) {
		this.props.showModal({
			open: true,
			title: i18next.t("magicPlan.deleteRestEmployee"),
			style: { width: '350px' },
			message: i18next.t("magicPlan.deleteRestEmployeeConfirm"),
			deleteAction: () => this.confirmDeleteSeparateRest(employee_id),
			closeModal: this.closeModal
		}, 'delete')
	}

	confirmDeleteSeparateRest = async (employee_id) => {
		const response = await employeesService.removeSeparatedRestEmployee(this.props.employee.id, employee_id);

		if (!response.ok) {
			let textError = errorFormating(response);
			this.openAlertModal(textError.id);
		}
		else {
			toastr.success(i18next.t("common.success"), i18next.t("magicPlan.deleteRestEmployeeSuccess"));
			this.getSeparatedRestEmployees();
			this.notifyDataChanged();
			this.closeModal();
		}
	}

	// modales turnos

	openNewRestrictedShiftModal() {
		const { employee } = this.props;

		this.props.showModal({
			open: true,
			title: i18next.t("magicPlan.newRestShift"),
			style: { width: '350px' },
			content: (
				<EmployeeRestrictedShiftEdit
					handleClose={this.closeModal}
					getRestrictedShifts={this.getRestrictedShiftsEmployees}
					employee={employee}
					onDataChanged={this.props.onDataChanged}
				/>
			),
			closeModal: this.closeModal
		}, 'edit')
	}

	openDeleteRestrictedShiftModal(shift_id) {
		this.props.showModal({
			open: true,
			title: i18next.t("magicPlan.deleteRestEmployee"),
			style: { width: '350px' },
			message: i18next.t("magicPlan.deleteRestShiftConfirm"),
			deleteAction: () => this.confirmDeleteRestrictedShift(shift_id),
			closeModal: this.closeModal
		}, 'delete')
	}

	confirmDeleteRestrictedShift = async (shift_id) => {
		const response = await employeesService.removeRestrictedShiftEmployee(this.props.employee.id, shift_id);

		if (!response.ok) {
			let textError = errorFormating(response);
			this.openAlertModal(textError.id);
		}
		else {
			toastr.success(i18next.t("common.success"), i18next.t("magicPlan.deleteRestShiftSuccess"));
			this.getRestrictedShiftsEmployees();
			this.notifyDataChanged();
			this.closeModal();
		}
	}

	// modales backup

	openNewEmployeeBackupModal() {
		const { employee } = this.props;

		this.props.showModal({
			open: true,
			title: i18next.t("magicPlan.addBackup"),
			style: { width: '300px' },
			content: (
				<EmployeeBackupEdit
					handleClose={this.closeModal}
					getSeparatedRestBackup={this.getSeparatedRestBackup}
					employee={employee}
					onDataChanged={this.props.onDataChanged}
				/>
			),
			closeModal: this.closeModal
		}, 'edit')
	}

	openDeleteEmployeeBackupModal() {
		this.props.showModal({
			open: true,
			title: i18next.t("magicPlan.deleteBackup"),
			style: { width: '350px' },
			message: i18next.t("magicPlan.deleteBackupConfirm"),
			deleteAction: () => this.confirmDeleteBackup(),
			closeModal: this.closeModal
		}, 'delete')
	}

	confirmDeleteBackup = async () => {
		const response = await employeesService.removeSeparatedRestsBackup(this.props.employee.id);

		if (!response.ok) {
			let textError = errorFormating(response);
			this.openAlertModal(textError.id);
		}
		else {
			this.getSeparatedRestBackup();
			this.notifyDataChanged();
			this.closeModal();
		}
	}



	// resto...

	getEmptyRow = (tipo) => {
		return (
			<tr>
				<td colSpan="2" className="no-result">
					{tipo === "empleado" ? i18next.t("magicPlan.emptyEmployee") : i18next.t("magicPlan.emptyShift")}
				</td>
			</tr>
		);
	}

	getEmployeeRows = () => this.props.employee_separated_rest_employees.map((employee_separated) => {
		return (
			<tr key={employee_separated.id}>
				<td>
					{employee_separated.name} {employee_separated.surname}
				</td>
				<td className="td-actions">
					<Dropdown>
						<Dropdown.Toggle variant="action"><FiMoreHorizontal /></Dropdown.Toggle>
						<Dropdown.Menu>
							<Dropdown.Item as="button" onClick={() => this.openDeleteEmployeeSeparatedRestModal(employee_separated.id)}>{i18next.t("magicPlan.deleteAction")}</Dropdown.Item>
						</Dropdown.Menu>
					</Dropdown>
				</td>
			</tr>
		)
	});

	renderDayTypeConfigSaveButton = () => {
		const { hasUnsavedHoursChanges, isSavingHours } = this.state;

		if (!hasUnsavedHoursChanges) return null;

		return (
			<div className="text-right" style={{ textAlign: 'right' }}>
				<button
					type="button"
					className="btn btn-primary"
					onClick={this.saveHoursChanges}
					disabled={isSavingHours}
				>
					{isSavingHours ? i18next.t("magicPlan.saving") : i18next.t("magicPlan.save")}
				</button>
			</div>
		);
	};

	renderShiftConfigSaveButton = () => {
		const { hasUnsavedShiftsChanges, isSavingShifts } = this.state;

		if (!hasUnsavedShiftsChanges) return null;

		return (
			<div className="text-right" style={{ textAlign: 'right' }}>
				<button
					type="button"
					className="btn btn-primary"
					onClick={this.saveShiftsChanges}
					disabled={isSavingShifts}
				>
					{isSavingShifts ? i18next.t("magicPlan.saving") : i18next.t("magicPlan.save")}
				</button>
			</div>
		);
	};

	getRestWindowBetweenBlocksContent = () => {
		const { select_day_types } = this.props;

		if (!select_day_types || select_day_types.length === 0) return null;

		return (
			<div className="row-adding staffing-level-edit employee-rest-window-section">
				<p>{i18next.t("magicPlan.restWindowTitle")}</p>
				<div className="table-wrapper">
					<table className="table table-bordered table-condensed table-days table-staffing-needs employee-hours-restrictions employee-rest-window-restrictions">
						<thead>
							<tr>
								<th></th>
								{select_day_types.map((day_type) => (
									<th className="th-daytype" key={day_type.id}>
										<div className="th-wrap">
											{day_type.name}
										</div>
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							<tr>
								<th>
									<div className="th-wrap">{i18next.t("magicPlan.restWindowStart")}</div>
								</th>
								{select_day_types.map((day_type) => {
									const restWindowStart = this.getRestWindowBetweenBlocksStartByDayType(day_type.id);
									return (
										<td key={`rest-window-start-${day_type.id}`} className={restWindowStart ? "filled" : "empty"}>
											<div className="hours-restriction-time">
												<TimeInputWithArrows
													id={`rest-window-start-${day_type.id}`}
													value={restWindowStart}
													className="hours-restriction-time__input"
													placeholder="hh:mm"
													showDecorators={false}
													nextInputId={`rest-window-end-${day_type.id}`}
													onChange={(event) => this.handleDayTypeConfigTimeChange(
														event,
														day_type.id,
														'rest_window_between_blocks_start'
													)}
												/>
											</div>
										</td>
									);
								})}
							</tr>
							<tr>
								<th>
									<div className="th-wrap">{i18next.t("magicPlan.restWindowEnd")}</div>
								</th>
								{select_day_types.map((day_type) => {
									const restWindowEnd = this.getRestWindowBetweenBlocksEndByDayType(day_type.id);
									return (
										<td key={`rest-window-end-${day_type.id}`} className={restWindowEnd ? "filled" : "empty"}>
											<div className="hours-restriction-time">
												<TimeInputWithArrows
													id={`rest-window-end-${day_type.id}`}
													value={restWindowEnd}
													className="hours-restriction-time__input"
													placeholder="hh:mm"
													showDecorators={false}
													onChange={(event) => this.handleDayTypeConfigTimeChange(
														event,
														day_type.id,
														'rest_window_between_blocks_end'
													)}
												/>
											</div>
										</td>
									);
								})}
							</tr>
						</tbody>
					</table>
				</div>
				{this.renderDayTypeConfigSaveButton()}
			</div>
		);
	};

	getRestriccionesDescansoContent = () => {
		const { employee_separated_rest_employees } = this.props;

		return (
			<>
				<div className="row-adding">
					<p>{i18next.t("magicPlan.separatedRestTitle")}</p>

					<table className="table table-separated-rests table-zebra-reverse">
						<thead>
							<tr>
								<th>{i18next.t("magicPlan.colEmployee")}</th>
								<th className="td-actions"><button type="button" title="Asignar empleado" onClick={this.openNewEmployeeSeparatedRestModal} className="btn btn-new"><FiPlus /></button></th>
							</tr>
						</thead>
						<tbody>
							{employee_separated_rest_employees.length === 0 ?
								this.getEmptyRow("empleado")
								:
								this.getEmployeeRows()
							}
						</tbody>
					</table>
				</div>
			</>
		);
	}


	dayTypeConfigHasValues = (item) => restrictionItemHasValues(item);

	applyRestrictionConfig = (day_type_config) => {
		const updatedEmployee = { ...this.state.employee };
		if (day_type_config.length) {
			updatedEmployee.day_type_config = day_type_config;
		} else {
			delete updatedEmployee.day_type_config;
		}
		const baseline = this.state.prevEmployee.day_type_config || [];
		this.setState({
			employee: updatedEmployee,
			hasUnsavedHoursChanges: restrictionSignature(day_type_config) !== restrictionSignature(baseline),
		});
	};

	handleDayTypeConfigTimeChange = (event, dayTypeId, field) => {
		const value = (event.target.value || '').trim();

		const { employee } = this.state;
		let day_type_config = employee.day_type_config ? [...employee.day_type_config] : [];

		const existingIndex = day_type_config.findIndex(item => item.day_type_id === dayTypeId);

		let newItem;
		if (existingIndex >= 0) {
			newItem = { ...day_type_config[existingIndex], [field]: value };
		} else {
			newItem = { day_type_id: dayTypeId, [field]: value };
		}

		if (!value) {
			delete newItem[field];
		}

		if (this.dayTypeConfigHasValues(newItem)) {
			if (existingIndex >= 0) {
				day_type_config[existingIndex] = newItem;
			} else {
				day_type_config.push(newItem);
			}
		} else if (existingIndex >= 0) {
			day_type_config.splice(existingIndex, 1);
		}

		const configHasValues = day_type_config.length > 0;
		const updatedEmployee = { ...employee };

		if (configHasValues) {
			updatedEmployee.day_type_config = day_type_config;
		} else {
			delete updatedEmployee.day_type_config;
		}

		this.setState({
			employee: updatedEmployee,
			hasUnsavedHoursChanges: true
		});
	};

	saveHoursChanges = async () => {
		this.setState({ isSavingHours: true });

		const { employee } = this.state;

		// Parche previo (backend sin merge correcto en day_type_config): primer PUT vacío y luego el payload.
		// const employeeClearToSave = { ...employee };
		// delete employeeClearToSave.day_type_config_response;
		// delete employeeClearToSave.day_type_config;
		// const responseClear = await employeesService.update(employeeClearToSave);
		// if (!responseClear.ok) {
		// 	this.setState({
		// 		errors: errorFormating(responseClear),
		// 		isSavingHours: false
		// 	});
		// 	toastr.error("Error", "No se pudo limpiar la configuración anterior.");
		// 	return;
		// }

		const employeeToSave = { ...employee };
		delete employeeToSave.day_type_config_response;
		delete employeeToSave.shift_config_response;

		const response = await employeesService.update(employeeToSave);

		if (response.ok) {
			toastr.success(i18next.t("common.success"), i18next.t("magicPlan.saveRestrictionsSuccess"));
			this.publishEmployee(employee);
			this.notifyDataChanged();
			this.setState({
				hasUnsavedHoursChanges: false,
				prevEmployee: employee,
				isSavingHours: false,
				errors: {}
			});
		} else {
			const formattedErrors = errorFormating(response);
			const errorMessage =
				formattedErrors?.general
				|| response?.errors?.[0]?.description
				|| i18next.t("magicPlan.saveRestrictionsError");
			toastr.error("Error", errorMessage);
			this.setState({
				errors: formattedErrors,
				isSavingHours: false
			});
		}
	};

	getRestWindowBetweenBlocksStartByDayType = (dayTypeId) => {
		const { employee } = this.state;
		if (!employee.day_type_config) return '';

		const item = employee.day_type_config.find(item => item.day_type_id === dayTypeId);
		return item?.rest_window_between_blocks_start || '';
	};

	getRestWindowBetweenBlocksEndByDayType = (dayTypeId) => {
		const { employee } = this.state;
		if (!employee.day_type_config) return '';

		const item = employee.day_type_config.find(item => item.day_type_id === dayTypeId);
		return item?.rest_window_between_blocks_end || '';
	};

	getRestriccionesHorasContent = () => {
		const { select_day_types } = this.props;

		if (!select_day_types || select_day_types.length === 0) return null;

		const rows = [
			{ id: "max_daily_working_hours", label: i18next.t("magicPlan.maxHoursPerDay") },
			{ id: "min_rest_hours_between_blocks", label: i18next.t("magicPlan.minRestBetweenBlocks") },
			{ id: "max_rest_hours_between_blocks", label: i18next.t("magicPlan.maxRestBetweenBlocks") },
			{ id: "max_number_of_blocks", label: i18next.t("magicPlan.maxBlocks") },
		];

		return (
			<div className="row-adding">
				<EmployeeDayTypeGrid
					columns={select_day_types}
					rows={rows}
					values={this.state.employee.day_type_config || []}
					onChange={this.applyRestrictionConfig}
				/>
				{this.renderDayTypeConfigSaveButton()}
			</div>
		);
	}

	handleNewShiftConfigChange = (event) => {
		const { id, value } = event.target;
		const field = id === "new_shift_config_shift"
			? "shift_id"
			: id === "new_shift_config_priority"
				? "priority"
				: "max_weekly_assignments";
		this.setState((prev) => ({
			new_shift_config: {
				...prev.new_shift_config,
				[field]: value,
			},
		}));
	};

	getShiftConfigItems = () => {
		const { employee } = this.state;
		const { select_shifts } = this.props;

		if (!employee.shift_config || !select_shifts) return [];

		return employee.shift_config.map((config) => {
			const shift = select_shifts.find((item) => item.id === config.shift_id);
			return {
				...config,
				name: shift?.name || "",
				time_intervals: shift?.time_intervals || [],
			};
		});
	};

	addShiftConfigLimit = () => {
		const { new_shift_config, employee } = this.state;
		const { select_shifts } = this.props;
		const shiftId = parseInt(new_shift_config.shift_id, 10);
		const parsedMaxWeeklyAssignments = parseInt(new_shift_config.max_weekly_assignments, 10);
		const parsedPriority = parseInt(new_shift_config.priority, 10);
		const maxWeeklyAssignments = parsedMaxWeeklyAssignments > 0 ? parsedMaxWeeklyAssignments : null;
		const priority = parsedPriority > 0 ? parsedPriority : null;

		if (!shiftId || (!priority && !maxWeeklyAssignments)) return;

		const shift = select_shifts.find((item) => item.id === shiftId);
		if (!shift) return;

		const shift_config = [...(employee.shift_config || []), {
			shift_id: shiftId,
			priority,
			max_weekly_assignments: maxWeeklyAssignments,
		}];

		this.setState({
			employee: { ...employee, shift_config },
			new_shift_config: { shift_id: "", max_weekly_assignments: "", priority: "" },
			hasUnsavedShiftsChanges: true,
		});
	};

	updateShiftPriority = (shiftId, value) => {
		const { employee } = this.state;
		const parsedShiftId = parseInt(shiftId, 10);
		const priority = value === "" ? null : parseInt(value, 10);
		const existing = (employee.shift_config || []).find((item) => item.shift_id === parsedShiftId);
		const shift_config = existing
			? employee.shift_config.map((item) => item.shift_id === parsedShiftId ? { ...item, priority } : item)
			: [...(employee.shift_config || []), { shift_id: parsedShiftId, priority }];

		this.setState({ employee: { ...employee, shift_config }, hasUnsavedShiftsChanges: true });
	};

	removeShiftConfigLimit = (shiftId) => {
		const { employee } = this.state;
		const parsedShiftId = parseInt(shiftId, 10);
		const shift_config = (employee.shift_config || []).filter((item) => item.shift_id !== parsedShiftId);
		const updatedEmployee = { ...employee };

		if (shift_config.length > 0) {
			updatedEmployee.shift_config = shift_config;
		} else {
			delete updatedEmployee.shift_config;
		}

		this.setState({
			employee: updatedEmployee,
			hasUnsavedShiftsChanges: true,
		});
	};

	updateShiftConfigLimitValue = (shiftId, value) => {
		const { employee } = this.state;
		const parsedShiftId = parseInt(shiftId, 10);
		const shift_config = (employee.shift_config || []).map((item) => (
			item.shift_id === parsedShiftId
				? { ...item, max_weekly_assignments: value }
				: item
		));

		this.setState({
			employee: { ...employee, shift_config },
			hasUnsavedShiftsChanges: true,
		});
	};

	commitShiftConfigLimitValue = (shiftId, value) => {
		const maxWeeklyAssignments = parseInt(value, 10);
		this.updateShiftConfigLimitValue(shiftId, maxWeeklyAssignments > 0 ? maxWeeklyAssignments : null);
	};

	normalizeShiftConfigForSave = (shift_config) => {
		if (!shift_config || shift_config.length === 0) return undefined;

		const normalized = shift_config
			.map((item) => {
				const maxWeeklyAssignments = parseInt(item.max_weekly_assignments, 10);
				const priority = parseInt(item.priority, 10);
				return {
					...item,
					max_weekly_assignments: maxWeeklyAssignments > 0 ? maxWeeklyAssignments : null,
					priority: priority > 0 ? priority : null,
				};
			})
			.filter((item) => item.max_weekly_assignments > 0 || item.priority > 0);

		return normalized.length > 0 ? normalized : undefined;
	};

	getSelectedShiftConfigItems = () =>
		this.getShiftConfigItems().map((item) => (
			<li key={item.shift_id} className="shift-weekly-limit-item tag-default tag-delete">
				<span className="shift-weekly-limit-item__name" title={item.name}>
					{item.name} {getFirstLastInterval(item.time_intervals, true)}
				</span>
				<FormControl
					as="select"
					className="shift-weekly-limit-item__priority"
					value={item.priority ?? ""}
					onChange={(event) => this.updateShiftPriority(item.shift_id, event.target.value)}
				>
					<option value="">{i18next.t("magicPlan.selectPriority")}</option>
					{[...Array(10).keys()].map((i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
					<option value="100">100</option>
					<option value="1000">1000</option>
				</FormControl>
				<FormGroup className="time-display shift-weekly-limit-item__input">
					<InputGroup>
						<FormControl
							type="text"
							value={item.max_weekly_assignments ?? ""}
							onChange={(event) => this.updateShiftConfigLimitValue(item.shift_id, event.target.value)}
							onBlur={(event) => this.commitShiftConfigLimitValue(item.shift_id, event.target.value)}
							placeholder="0"
						/>
						<InputGroup.Text>
							{i18next.t('magicPlan.unitTimes', { count: parseInt(item.max_weekly_assignments, 10) || 0 })}
						</InputGroup.Text>
					</InputGroup>
				</FormGroup>
				<button
					type="button"
					onClick={() => this.removeShiftConfigLimit(item.shift_id)}
					className="btn-tag-delete btn-transparent"
				>
					<FiX />
				</button>
			</li>
		));

	formatShiftIntervalLabels = (time_intervals) => {
		if (!time_intervals || time_intervals.length === 0) return [];
		return time_intervals.map((item) => {
			const start = convertFromUTC(item.start);
			const end = convertFromUTC(item.end);
			const short = (t) => {
				if (!t) return "";
				const [h, m] = t.split(":");
				return m === "00" ? h : `${h}:${m}`;
			};
			return `${short(start)}-${short(end)}h`;
		});
	};

	renderRestrictedShiftCell = (restricted_shift) => {
		const textColor = getContrastColor(restricted_shift.background_color, "#55536b");
		const title = `${restricted_shift.name}${
			restricted_shift.description ? ` - ${restricted_shift.description}` : ""
		}`;

		return (
			<div style={{ maxWidth: "280px", overflow: "hidden" }}>
				<ShiftPatternShiftTag
					className="sp-shift-tag--sm"
					name={restricted_shift.name}
					intervalLabels={this.formatShiftIntervalLabels(restricted_shift.time_intervals)}
					style={{
						backgroundColor: restricted_shift.background_color || undefined,
						borderColor: restricted_shift.border_color
							? restricted_shift.border_color
							: restricted_shift.background_color || undefined,
						color: textColor,
						width: "100%",
						maxWidth: "100%",
					}}
					title={title}
				/>
			</div>
		);
	};

	renderRestrictedShiftActions = (restricted_shift) => (
		<Dropdown align="end">
			<Dropdown.Toggle variant="action">
				<FiMoreHorizontal />
			</Dropdown.Toggle>
			<Dropdown.Menu>
				<Dropdown.Item
					as="button"
					className="text-danger"
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						this.openDeleteRestrictedShiftModal(restricted_shift.id);
					}}
				>
					{i18next.t("magicPlan.deleteAction")}
				</Dropdown.Item>
			</Dropdown.Menu>
		</Dropdown>
	);

	saveShiftsChanges = async () => {
		this.setState({ isSavingShifts: true });

		const { employee } = this.state;
		const employeeToSave = { ...employee };
		delete employeeToSave.day_type_config_response;
		delete employeeToSave.shift_config_response;

		const normalizedShiftConfig = this.normalizeShiftConfigForSave(employeeToSave.shift_config);
		if (normalizedShiftConfig) {
			employeeToSave.shift_config = normalizedShiftConfig;
		} else {
			delete employeeToSave.shift_config;
		}

		const response = await employeesService.update(employeeToSave);

		if (response.ok) {
			toastr.success(i18next.t("common.success"), i18next.t("magicPlan.saveRestrictionsSuccess"));
			this.publishEmployee(employee);
			this.notifyDataChanged();
			this.setState({
				hasUnsavedShiftsChanges: false,
				prevEmployee: employee,
				isSavingShifts: false,
				errors: {}
			});
		} else {
			const formattedErrors = errorFormating(response);
			const errorMessage =
				formattedErrors?.general
				|| response?.errors?.[0]?.description
				|| i18next.t("magicPlan.saveRestrictionsError");
			toastr.error("Error", errorMessage);
			this.setState({
				errors: formattedErrors,
				isSavingShifts: false
			});
		}
	};

	getShiftConfigContent = () => {
		const { select_shifts } = this.props;
		const { new_shift_config } = this.state;
		const shiftConfigItems = this.getShiftConfigItems();

		if (!select_shifts || select_shifts.length === 0) return null;

		const optionShiftConfigList = select_shifts
			.filter((item) => !shiftConfigItems.some((config) => config.shift_id === item.id))
			.map((item) => (
				<option key={item.id} value={item.id}>
					{item.name} {getFirstLastInterval(item.time_intervals, true)}
				</option>
			));

		return (
			<div className="row-adding employee-shift-config-section">
				<div className="shift-weekly-limit-add">
					<FormLabel className="shift-weekly-limit-add__label">
						{i18next.t("magicPlan.labelPrioritizeShiftAssignments")}
					</FormLabel>
					<div className="shift-weekly-limit-add__row">
						<FormControl as="select" id="new_shift_config_shift" value={new_shift_config.shift_id} onChange={this.handleNewShiftConfigChange} className="shift-weekly-limit-add__shift">
							<option value="">{i18next.t("magicPlan.selectShift")}</option>
							{optionShiftConfigList}
						</FormControl>
						<FormControl className="shift-weekly-limit-add__priority" as="select" id="new_shift_config_priority" value={new_shift_config.priority} onChange={this.handleNewShiftConfigChange}>
							<option value="">{i18next.t("magicPlan.selectPriority")}</option>
							{[...Array(10).keys()].map((i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
							<option value="100">100</option><option value="1000">1000</option>
						</FormControl>
						<FormGroup className="time-display" controlId="new_shift_config_value">
							<InputGroup>
								<FormControl
									type="text"
									id="new_shift_config_value"
									value={new_shift_config.max_weekly_assignments}
									onChange={this.handleNewShiftConfigChange}
									placeholder="0"
								/>
								<InputGroup.Text>
									{i18next.t('magicPlan.unitTimes', { count: parseInt(new_shift_config.max_weekly_assignments, 10) || 0 })}
								</InputGroup.Text>
							</InputGroup>
						</FormGroup>
						<button
							type="button"
							onClick={this.addShiftConfigLimit}
							className="btn btn-primary shift-weekly-limit-add__btn"
						>
							{i18next.t('common.add')}
						</button>
					</div>
				</div>
				{shiftConfigItems.length > 0 && (
					<ul className="list-tags shift-weekly-limit-list">{this.getSelectedShiftConfigItems()}</ul>
				)}
				{this.renderShiftConfigSaveButton()}
			</div>
		);
	};

	getRestriccionesTurnoContent = () => {
		const { employee_restricted_shifts } = this.props;

		return (
			<div className="row-adding employee-restricted-shifts-section">
				<p>{i18next.t("magicPlan.restrictedShiftsTitle")}</p>

				<DataTable
					tableClassName="table-shift-patterns table-hover sp-shift-pattern-employees-table"
					tableStyle={{ backgroundColor: "#ffffff" }}
					columns={[
						{
							key: "shift",
							header: i18next.t("magicPlan.colShift"),
							sortable: false,
							render: this.renderRestrictedShiftCell,
						},
					]}
					rows={employee_restricted_shifts}
					rowKey={(row) => row.id}
					emptyMessage={i18next.t("magicPlan.emptyShift")}
					showHeaderSortAffix={false}
					actionsColumn={{
						header: (
							<button
								type="button"
								title={i18next.t("magicPlan.newRestShift")}
								onClick={this.openNewRestrictedShiftModal}
								className="btn btn-new"
							>
								<FiPlus />
							</button>
						),
						headerClassName: "td-actions text-end",
						className: "td-actions",
						render: this.renderRestrictedShiftActions,
					}}
				/>
			</div>
		);
	}


	renderHoursSection = () => {
		const { employee, maxHoursBetweenRestDraft, errors } = this.state;
		const exactDailyHoursByContract = employee.exact_daily_hours_by_contract;
		const maxHoursError = errors.max_working_hours_between_restdays;

		return (
			<>
				<Row className='mb30'>
					<Col sm={12}>
						<FormGroup className='form-group form-group-flex form-row-switch' controlId="exact_daily_hours_by_contract">
							<FormLabel className="inline-block">{i18next.t("magicPlan.exactDailyHours")}</FormLabel>
							<Switch
								controlId="exact_daily_hours_by_contract"
								isOn={exactDailyHoursByContract}
								handleToggle={this.handleSwitch}
							/>
						</FormGroup>
						<FormGroup
							className="form-group form-group-flex form-row-switch employee-magic-plan__max-hours"
							controlId="max_working_hours_between_restdays"
						>
							<FormLabel className="inline-block">
								{i18next.t("magicPlan.maxHoursBeforeRest")}
							</FormLabel>
							<InputGroup className="employee-magic-plan__max-hours-input">
								<FormControl
									type="text"
									inputMode="numeric"
									value={maxHoursBetweenRestDraft}
									onChange={this.handleMaxHoursBetweenRestChange}
									onBlur={this.handleMaxHoursBetweenRestBlur}
									onKeyDown={this.handleMaxHoursBetweenRestKeyDown}
									isInvalid={Boolean(maxHoursError)}
									aria-label={i18next.t("magicPlan.maxHoursBeforeRest")}
								/>
								<InputGroup.Text>{i18next.t("magicPlan.unitHours")}</InputGroup.Text>
							</InputGroup>
						</FormGroup>
						{maxHoursError ? (
							<div className="invalid-feedback d-block">{maxHoursError}</div>
						) : null}
					</Col>
				</Row>

				{this.getRestriccionesHorasContent()}
			</>
		);
	};

	renderRestSection = () => {
		const { employee_backup } = this.props;

		return (
			<>
				{this.getRestWindowBetweenBlocksContent()}
				{this.getRestriccionesDescansoContent()}

				{employee_backup && employee_backup.name ? (
					<div className="employee-backup">
						<label className="form-label">{i18next.t("magicPlan.backupEmployee")}</label>
						<span className="employee-backup-name">{employee_backup.name} {employee_backup.surname}</span>
						<button type="button" title="Modificar empleado sustituto" onClick={this.openNewEmployeeBackupModal} className="btn btn-icon"><FiEdit3 /></button>
						<button type="button" title="Eliminar empleado sustituto" onClick={this.openDeleteEmployeeBackupModal} className="btn btn-icon"><FiTrash2 /></button>
					</div>
				) :
					<button type="button" title={i18next.t("magicPlan.addBackup")} onClick={this.openNewEmployeeBackupModal} className="btn btn-primary"><FiPlus /> {i18next.t("magicPlan.addBackup")}</button>
				}
			</>
		);
	};

	renderShiftsSection = () => {
		const { employee } = this.state;

		return (
			<>
				<Row className="mb30">
					<Col sm={12}>
						<FormGroup className="form-group form-group-flex form-row-switch" controlId="must_never_be_alone">
							<FormLabel className="inline-block">{i18next.t("magicPlan.neverAlone")}</FormLabel>
							<Switch
								controlId="must_never_be_alone"
								isOn={employee.must_never_be_alone}
								handleToggle={this.handleSwitch}
							/>
						</FormGroup>
						<FormGroup className="form-group form-group-flex form-row-switch" controlId="high_load">
							<FormLabel className="employee-magic-plan__label-with-info">
								{i18next.t("magicPlan.highLoad")}
								<OverlayTrigger
									placement="top"
									overlay={(
										<Tooltip id="tooltip-high-load" className="employee-magic-plan__tooltip">
											{i18next.t("magicPlan.tooltipHighLoad")}
										</Tooltip>
									)}
								>
									<button
										type="button"
										className="btn-transparent employee-magic-plan__info-btn"
										aria-label={i18next.t("magicPlan.ariaHighLoad")}
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
										}}
									>
										<FiInfo size={14} />
									</button>
								</OverlayTrigger>
							</FormLabel>
							<Switch
								controlId="high_load"
								isOn={employee.high_load}
								handleToggle={this.handleSwitch}
							/>
						</FormGroup>
					</Col>
				</Row>

				{this.getRestriccionesTurnoContent()}
				{this.getShiftConfigContent()}
			</>
		);
	};

	renderActiveSection = () => {
		switch (this.state.activeSection) {
			case SECTION_REST:
				return this.renderRestSection();
			case SECTION_SHIFTS:
				return this.renderShiftsSection();
			case SECTION_HOURS:
			default:
				return this.renderHoursSection();
		}
	};

	render() {
		const { activeSection } = this.state;
		const sectionLabels = {
			[SECTION_HOURS]: i18next.t("magicPlan.sectionHours"),
			[SECTION_REST]: i18next.t("magicPlan.sectionRest"),
			[SECTION_SHIFTS]: i18next.t("magicPlan.sectionShifts"),
		};

		return (
			<div className="employee-magic-plan">
				<div className="employee-magic-plan__switch" role="tablist">
					{SECTIONS.map((section) => {
						const isActive = activeSection === section.key;
						return (
							<button
								key={section.key}
								type="button"
								role="tab"
								aria-selected={isActive}
								className={`employee-magic-plan__switch-btn ${isActive ? "employee-magic-plan__switch-btn--active" : ""}`}
								onClick={() => this.setActiveSection(section.key)}
							>
								{sectionLabels[section.key]}
							</button>
						);
					})}
				</div>

				{this.renderActiveSection()}
			</div>
		);
	}
}


const mapStateToProps = state => {
	return {
		employee_separated_rest_employees: state.employeesReducer.employee_separated_rest_employees,
		employee_restricted_shifts: state.employeesReducer.employee_restricted_shifts,
		employee_backup: state.employeesReducer.employee_backup,
		select_employees: state.employeesReducer.select_employees,
		user: state.authReducer.user,
		select_day_types: state.calendarsReducer.select_day_types,
		select_shifts: state.shiftsReducer.select_shifts,
	}
}

const mapDispatchToProps = dispatch => ({
	hideModal: () => dispatch(hideModal()),
	showModal: (modalProps, modalType) => {
		dispatch(showModal({ modalProps, modalType }))
	},
	getEmployeeSeparatedRestEmployees: (id) => dispatch(employeesActions.getEmployeeSeparatedRestEmployees(id)),
	getEmployeeRestrictedShifts: (id) => dispatch(employeesActions.getEmployeeRestrictedShifts(id)),
	getEmployeeSeparatedRestBackup: (id) => dispatch(employeesActions.getEmployeeSeparatedRestBackup(id)),
	getSelectableEmployees: (options) => dispatch(employeesActions.getSelectableEmployees(options)),
	getSelectableShifts: () => dispatch(shiftsActions.getSelectableShifts()),
	getSelectableDayTypes: () => dispatch(calendarsActions.getSelectableDayTypes()),
})

export default connect(mapStateToProps, mapDispatchToProps)(EmployeeMagicPlan);
